import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bug,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Info,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { api } from '@/services/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/shared/StatCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface ErrorLog {
  id: string;
  created_at: string;
  severity: string;
  error_type: string;
  message: string;
  url?: string;
  stack_trace?: string | null;
  metadata?: Record<string, unknown> | null;
  user?: { id: string; full_name: string; email: string } | null;
}

interface ErrorLogsResponse {
  data: ErrorLog[];
  pagination: { cursor: string | null; has_more: boolean; total_count: number };
}

interface ErrorStats {
  last_24_hours: number;
  last_7_days: number;
  by_severity: { critical: number; error: number; warning: number; info: number };
}

const severityConfig: Record<string, { icon: typeof XCircle; color: string; bg: string }> = {
  critical: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900/30' },
  error: { icon: AlertCircle, color: 'text-orange-600', bg: 'bg-orange-100 dark:bg-orange-900/30' },
  warning: { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-100 dark:bg-yellow-900/30' },
  info: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30' },
};

function getSeverityBadge(severity: string) {
  const config = severityConfig[severity] || severityConfig.info;
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`${config.bg} ${config.color} border-0`}>
      <Icon className="h-3 w-3 mr-1" />
      {severity}
    </Badge>
  );
}

export function ErrorLogsPage() {
  const [severityFilter, setSeverityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const params: Record<string, string> = {};
  if (severityFilter !== 'all') params.severity = severityFilter;
  if (typeFilter !== 'all') params.error_type = typeFilter;
  if (cursor) params.cursor = cursor;

  const { data, isLoading, refetch } = useQuery<ErrorLogsResponse>({
    queryKey: ['admin-error-logs', params],
    queryFn: () => api.get('/admin/error-logs', { params }).then((r) => r.data),
  });

  const { data: statsRes } = useQuery<{ data: ErrorStats }>({
    queryKey: ['admin-error-stats'],
    queryFn: () => api.get('/admin/error-stats').then((r) => r.data),
  });

  const logs = data?.data ?? [];
  const pagination = data?.pagination;
  const stats = statsRes?.data;

  function resetPagination() {
    setCursor(undefined);
    setCursorStack([]);
  }

  function goNext() {
    if (pagination?.cursor) {
      setCursorStack((s) => [...s, cursor ?? '']);
      setCursor(pagination.cursor);
    }
  }

  function goPrev() {
    const stack = [...cursorStack];
    const prev = stack.pop();
    setCursorStack(stack);
    setCursor(prev || undefined);
  }

  function formatTimestamp(d: string) {
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  function formatFullTimestamp(d: string) {
    return new Date(d).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Error Logs"
        description="Monitor and track application errors across all users"
      />

      <div className="flex flex-col gap-6">
        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <StatCard icon={AlertCircle} label="Last 24 Hours" value={stats?.last_24_hours ?? 0} color="orange" />
          <StatCard icon={XCircle} label="Critical" value={stats?.by_severity.critical ?? 0} color="red" />
          <StatCard icon={AlertTriangle} label="Errors" value={stats?.by_severity.error ?? 0} color="orange" />
          <StatCard icon={Bug} label="Last 7 Days" value={stats?.last_7_days ?? 0} color="muted" />
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              <Select value={severityFilter} onValueChange={(v) => { setSeverityFilter(v); resetPagination(); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); resetPagination(); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="runtime">Runtime</SelectItem>
                  <SelectItem value="api">API</SelectItem>
                  <SelectItem value="validation">Validation</SelectItem>
                  <SelectItem value="auth">Auth</SelectItem>
                  <SelectItem value="network">Network</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : logs.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No error logs found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="max-w-[300px]">Message</TableHead>
                    <TableHead>User</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedError(log)}
                    >
                      <TableCell className="whitespace-nowrap">
                        {formatTimestamp(log.created_at)}
                      </TableCell>
                      <TableCell>{getSeverityBadge(log.severity)}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{log.error_type}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[300px] truncate">
                        {log.message}
                      </TableCell>
                      <TableCell>
                        {log.user?.full_name || 'Anonymous'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {pagination ? `${pagination.total_count} total entries` : ''}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={goPrev} disabled={cursorStack.length === 0}>
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!pagination?.has_more}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Error Details Dialog */}
      <Dialog open={!!selectedError} onOpenChange={() => setSelectedError(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Error Details
              {selectedError && getSeverityBadge(selectedError.severity)}
            </DialogTitle>
          </DialogHeader>
          {selectedError && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="min-w-0 overflow-hidden">
                  <span className="text-muted-foreground">Time:</span>
                  <p className="font-medium truncate">
                    {formatFullTimestamp(selectedError.created_at)}
                  </p>
                </div>
                <div className="min-w-0 overflow-hidden">
                  <span className="text-muted-foreground">Type:</span>
                  <p className="font-medium truncate">{selectedError.error_type}</p>
                </div>
                <div className="min-w-0 overflow-hidden">
                  <span className="text-muted-foreground">User:</span>
                  <p className="font-medium truncate">
                    {selectedError.user?.full_name || 'Anonymous'}
                  </p>
                </div>
              </div>

              <div>
                <span className="text-sm text-muted-foreground">Message:</span>
                <p className="mt-1 p-3 bg-muted rounded-lg font-mono text-sm break-all">
                  {selectedError.message}
                </p>
              </div>

              {selectedError.url && (
                <div>
                  <span className="text-sm text-muted-foreground">URL:</span>
                  <p className="mt-1 p-3 bg-muted rounded-lg font-mono text-sm break-all">
                    {selectedError.url}
                  </p>
                </div>
              )}

              {selectedError.stack_trace && (
                <div>
                  <span className="text-sm text-muted-foreground">Stack Trace:</span>
                  <ScrollArea className="h-[200px] mt-1">
                    <pre className="p-3 bg-muted rounded-lg font-mono text-xs whitespace-pre-wrap">
                      {selectedError.stack_trace}
                    </pre>
                  </ScrollArea>
                </div>
              )}

              {selectedError.metadata && Object.keys(selectedError.metadata).length > 0 && (
                <div>
                  <span className="text-sm text-muted-foreground">Metadata:</span>
                  <pre className="mt-1 p-3 bg-muted rounded-lg font-mono text-xs overflow-auto">
                    {JSON.stringify(selectedError.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
